// Pengolahan gambar tanda tangan di browser (tanpa dependensi): hapus background kertas → PNG transparan,
// lalu potong rapat. Fungsi murni di atas array RGBA agar mudah diuji.

const clamp = (v, a, b) => (v < a ? a : v > b ? b : v);
const smoothstep = (x) => {
  const t = clamp(x, 0, 1);
  return t * t * (3 - 2 * t);
};

const WARNA_TINTA = { hitam: [17, 17, 17], biru: [20, 40, 140] };

// Perkiraan kecerahan kertas per piksel: persentil ke-90 per petak, dihaluskan lalu diinterpolasi bilinear —
// bayangan/pencahayaan tak merata di foto ikut terkompensasi, goresan tinta (gelap) tak memengaruhi.
function petaKertas(gray, w, h) {
  const T = Math.max(8, Math.round(Math.min(w, h) / 36));
  const tx = Math.ceil(w / T);
  const ty = Math.ceil(h / T);
  let peta = new Float32Array(tx * ty);
  for (let by = 0; by < ty; by++) {
    for (let bx = 0; bx < tx; bx++) {
      const hist = new Uint32Array(64);
      let n = 0;
      for (let y = by * T; y < Math.min(h, (by + 1) * T); y++) {
        for (let x = bx * T; x < Math.min(w, (bx + 1) * T); x++) {
          hist[gray[y * w + x] >> 2]++;
          n++;
        }
      }
      let acc = 0;
      let bin = 63;
      for (let i = 0; i < 64; i++) {
        acc += hist[i];
        if (acc >= n * 0.9) {
          bin = i;
          break;
        }
      }
      peta[by * tx + bx] = bin * 4 + 2;
    }
  }
  for (let pass = 0; pass < 2; pass++) {
    const baru = new Float32Array(peta.length);
    for (let y = 0; y < ty; y++) {
      for (let x = 0; x < tx; x++) {
        let s = 0;
        let c = 0;
        for (let dy = -1; dy <= 1; dy++) {
          for (let dx = -1; dx <= 1; dx++) {
            const yy = y + dy;
            const xx = x + dx;
            if (yy >= 0 && yy < ty && xx >= 0 && xx < tx) {
              s += peta[yy * tx + xx];
              c++;
            }
          }
        }
        baru[y * tx + x] = s / c;
      }
    }
    peta = baru;
  }
  const bg = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    const fy = clamp((y + 0.5) / T - 0.5, 0, ty - 1);
    const y0 = Math.floor(fy);
    const y1 = Math.min(ty - 1, y0 + 1);
    const wy = fy - y0;
    for (let x = 0; x < w; x++) {
      const fx = clamp((x + 0.5) / T - 0.5, 0, tx - 1);
      const x0 = Math.floor(fx);
      const x1 = Math.min(tx - 1, x0 + 1);
      const wx = fx - x0;
      const a = peta[y0 * tx + x0] * (1 - wx) + peta[y0 * tx + x1] * wx;
      const b = peta[y1 * tx + x0] * (1 - wx) + peta[y1 * tx + x1] * wx;
      bg[y * w + x] = Math.max(60, a * (1 - wy) + b * wy);
    }
  }
  return bg;
}

/**
 * Hapus background kertas. rgba: Uint8ClampedArray/Uint8Array RGBA (w×h). Mengembalikan array RGBA baru.
 * kepekaan 0–100: makin tinggi makin banyak goresan tipis dipertahankan (makin rendah makin bersih dari noda).
 * warna: "asli" (tinta digelapkan, hue dipertahankan) | "hitam" | "biru".
 */
export function hapusBackground(rgba, w, h, { kepekaan = 55, warna = "asli" } = {}) {
  const gray = new Uint8Array(w * h);
  for (let i = 0; i < w * h; i++) {
    gray[i] = (rgba[i * 4] * 299 + rgba[i * 4 + 1] * 587 + rgba[i * 4 + 2] * 114) / 1000;
  }
  const bg = petaKertas(gray, w, h);
  const out = new Uint8ClampedArray(w * h * 4);
  const t0 = 0.3 - 0.25 * (clamp(kepekaan, 0, 100) / 100);
  const t1 = t0 + 0.2;
  const tetap = WARNA_TINTA[warna];
  for (let i = 0; i < w * h; i++) {
    const gelap = clamp((bg[i] - gray[i]) / bg[i], 0, 1);
    const a = smoothstep((gelap - t0) / (t1 - t0));
    if (a <= 0) continue;
    if (tetap) {
      out[i * 4] = tetap[0];
      out[i * 4 + 1] = tetap[1];
      out[i * 4 + 2] = tetap[2];
    } else {
      out[i * 4] = rgba[i * 4] * 0.55;
      out[i * 4 + 1] = rgba[i * 4 + 1] * 0.55;
      out[i * 4 + 2] = rgba[i * 4 + 2] * 0.55;
    }
    out[i * 4 + 3] = Math.round(a * 255);
  }
  return out;
}

/** Potong rapat ke kotak goresan (alpha > ambang) + bantalan. null bila kosong. */
export function potongRapat(rgba, w, h, bantalan = 6, ambang = 10) {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (rgba[(y * w + x) * 4 + 3] > ambang) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        if (y > y1) y1 = y;
      }
    }
  }
  if (x1 < 0) return null;
  x0 = Math.max(0, x0 - bantalan);
  y0 = Math.max(0, y0 - bantalan);
  x1 = Math.min(w - 1, x1 + bantalan);
  y1 = Math.min(h - 1, y1 + bantalan);
  const nw = x1 - x0 + 1;
  const nh = y1 - y0 + 1;
  const data = new Uint8ClampedArray(nw * nh * 4);
  for (let y = 0; y < nh; y++) {
    const dari = ((y0 + y) * w + x0) * 4;
    data.set(rgba.subarray(dari, dari + nw * 4), y * nw * 4);
  }
  return { data, width: nw, height: nh };
}

/** Persentase piksel bergoresan — untuk peringatan "hasil terlalu kosong / terlalu penuh". */
export function rasioTinta(rgba, w, h) {
  let n = 0;
  for (let i = 0; i < w * h; i++) if (rgba[i * 4 + 3] > 60) n++;
  return n / (w * h);
}
